import React, { useEffect, useState, useRef } from "react";
import {
  Accordion,
  Button,
  Col,
  Container,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import UtilMethods from "../../util/UtilMethods";
import FooterPanel from "../common/FooterPanel";
import MenuBar from "../common/MenuPanel";
import EventSupport from "./EventSupport"; // Import the EventSupport class
import { sendEmail } from "../../services/EmailSender";
import GroupMembersAccordion from "./GroupMembersAccordion";
import GroupRegistrationAccordion from "./GroupRegistrationAccordion";
import PaymentAccordion from "./PaymentAccordion";
import AlertModal from "../common/AlertModal";
import { orange } from "@mui/material/colors";
import ConsentModal from "../common/ConsentModal";
import waiverForm from "../../attachments/Krupabhishekam_Retreat_2025_Waiver.pdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faBan,
  faCheck,
  faSave,
  faFileAlt,
  faArrowLeft,
  faArrowRight,
  faDollar,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
/**
 * EventRegistrationPage component handles the registration process for a retreat event.
 * It fetches event and group data, manages form state, and handles user interactions
 * for saving, approving, and canceling registrations.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered EventRegistrationPage component.
 *
 * @example
 * <EventRegistrationPage />
 *
 * @description
 * This component uses several hooks and state variables to manage the registration process:
 * - `useParams` to get the event ID from the URL.
 * - `useLocation` to access the current location and query parameters.
 * - `useAuth` to get the authenticated user and user type.
 * - `useNavigate` to navigate between routes.
 * - `useState` to manage various state variables such as `eventData`, `groupInfo`, `loading`, `error`, etc.
 * - `useEffect` to fetch event and group data when the component mounts or when dependencies change.
 *
 * The component renders an accordion with event details and group registration forms.
 * It includes buttons for saving, approving, and canceling registrations, with appropriate validation and confirmation modals.
 *
 * @function
 * @name EventRegistrationPage
 *
 * @returns {JSX.Element} The rendered EventRegistrationPage component.
 */
const EventRegistrationPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const idMenuQuery = queryParams.get("id");
  const navMode = queryParams.get("mode");
  const { user, userType, logout } = useAuth();
  const accordionRef = useRef(null); // Ref for the accordion
  const navigate = useNavigate();
  const group = location.state?.group || null;

  const mode = location.state?.mode
    ? location.state.mode
    : userType === "G"
      ? "GUEST"
      : "USER"; // Default to "USER" if mode is missing
  // Access filters passed from the parent
  const parentFilters = location.state?.filters;

  const [activeKey, setActiveKey] = useState("1"); // Initially, open the second accordion
  const [editingMemberIndex, setEditingMemberIndex] = useState(null);
  const userData = user || null;
  const userEmail = userData?.email || "";
  const isAdmin = userType?.includes("A");
  const [isRegistrationAllowed, setIsRegistrationAllowed] = useState(true);
  const [newRegistrationStatus, setNewRegistrationStatus] = useState(null);
  const [otpVerified, setOTPVerified] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [groupInfo, setGroupInfo] = useState({
    registrationID: "",
    firstName: mode === "USER" ? userData?.firstName || "" : "",
    lastName: mode === "USER" ? userData?.lastName || "" : "",
    email: mode === "USER" ? userEmail || "" : "",
    phone: mode === "USER" ? userData?.phone || "" : "",
    userID: mode === "USER" ? userData?.userID || "" : "",
    address: mode === "USER" ? userData?.address || "" : "",
    city: mode === "USER" ? userData?.city || "" : "",
    state: mode === "USER" ? userData?.state || "" : "",
    zip: mode === "USER" ? userData?.zip || "" : "",
    country: mode === "USER" ? userData?.country || "" : "",
    specialRequest: "",
    rooms: 1,
    shareRoom: false,
    emergencyContactNumber: "",
    emergencyContactPersonName: "",
    paymentMethod: eventData?.paymentOptions?.split(",")[0]?.trim() || "Zelle", // Default to first option or "Zelle"
    paymentInfo: "",
    status: "Draft", // Default status
    refundStatus: "",
    refundAmount: "0",
    paidAmount: "0",
    waiverSigned: false,
    retreatID: id ? id : idMenuQuery,
    updatedBy: mode === "USER" ? userData?.userID || "" : "",
    numberOfParticipants: 1,
    createdAt: new Date().toISOString(),
    createdSource: "Web",
    groupMembers: [
      {
        memberID: "",
        firstName: mode === "USER" ? userData?.firstName || "" : "",
        lastName: mode === "USER" ? userData?.lastName || "" : "",
        email: mode === "USER" ? userEmail || "" : "",
        age: "0",
        gender: mode === "USER" ? userData?.gender || "" : "",
        phone: mode === "USER" ? userData?.phone || "" : "",
        relation: "Myself",
        comments: "",
        deleted: false,
        retreatID: id ? id : idMenuQuery,
        isEdited: false,
      },
    ],
  });
  const [originalGroupInfo, setOriginalGroupInfo] = useState({}); // To track original group info for comparison
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [saveMode, setSaveMode] = useState("MAIN"); // Default save mode

  // Confirmation modal states
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalType, setModalType] = useState("retreat"); // Default type
  const [showConsentModal, setShowConsentModal] = useState(false); // Modal visibility state

  useEffect(() => {
    const checkCanRegister = async () => {
      try {
        // Fetch event data
        const eventData = await EventSupport.fetchEventData(id || idMenuQuery);
        setEventData(eventData);
        // Perform registration check
        const currentDate = new Date();
        const registrationEndDate = new Date(eventData?.reg_end_date);
        const registrationStartDate = new Date(eventData?.reg_date);
        let newRegStatus;
        if (currentDate > registrationEndDate) {
          console.log("Registration has ended.");
          newRegStatus = "Blocked";
        } else if (currentDate < registrationStartDate) {
          console.log("Event is blocked.");
          newRegStatus = "Blocked";
        } else {
          newRegStatus = await EventSupport.getNewRegistrationStatus(eventData);
          newRegStatus = "New" === newRegStatus ? "Draft" : newRegStatus; // Default to "Draft" if there's an error
        }
        console.log("New registration status:", newRegStatus);
        const canRegisterResult =
          newRegStatus !== "Blocked" && currentDate <= registrationEndDate;
        // console.log("Can register result:", canRegisterResult);
        console.log("Can register result:", canRegisterResult);
        console.log("navMode:", navMode);
        setIsRegistrationAllowed(canRegisterResult);
        setNewRegistrationStatus(newRegStatus);
        // Redirect to login if registration is allowed but user is not authenticated
        if (canRegisterResult && !userData && navMode === "E") {
          console.log("mode E - Redirecting to login page");
          setActiveKey("1");
          localStorage.setItem("previousPath", location.pathname);
          navigate(`/login?${id ? `id=${id}` : `id=${idMenuQuery}`}`);
        } else if (canRegisterResult && !userData && navMode !== "E") {
          console.log("Not logged in - Viewing as guest");
          setActiveKey("0");
        }
        return newRegStatus;
      } catch (error) {
        console.error("Error checking registration or fetching data:", error);
        setIsRegistrationAllowed(false); // Default to disallow registration on error
      } finally {
        setLoading(false);
      }
    };

    const fetchGroupData = async (newStatus) => {
      try {
        let groupData;

        if (group) {
          groupData = group; // Use provided group data
        } else {
          // Fetch group data
          groupData = await EventSupport.fetchGroupData(
            id || idMenuQuery,
            userEmail,
            userData,
            mode
          );
        }
        if (!groupData?.registrationID) {
          groupData.status = newStatus;
        }

        // Update group info state
        setGroupInfo((prev) => ({ ...prev, ...groupData }));
        setOriginalGroupInfo({ ...groupData }); // Clone the group data for tracking changes

        // Enable registration if the group has a registration ID
        if (groupData?.registrationID) {
          setIsRegistrationAllowed(true);
        }
      } catch (err) {
        console.error("Error fetching group data:", err);
        setError("Failed to load group data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    // Main effect logic
    const initialize = async () => {
      setLoading(true);
      const newStatus = await checkCanRegister();
      if (userData) {
        await fetchGroupData(newStatus);
      }
      setLoading(false);
    };
    initialize();
  }, [
    id,
    idMenuQuery,
    userEmail,
    group,
    navigate,
    location.pathname,
    isAdmin,
    mode,
  ]);

  useEffect(() => {
    console.log("groupInfo.registrationID : ", groupInfo.registrationID);
    console.log("isRegistrationAllowed : ", isRegistrationAllowed);
    console.log("isAdmin : ", isAdmin);
    console.log("activeKey :", activeKey);
    if (!isRegistrationAllowed) {
      setActiveKey("0");
    } else if (
      groupInfo.status === "Draft" &&
      !isAdmin &&
      groupInfo.registrationID &&
      String(groupInfo.registrationID).trim() !== "" &&
      activeKey != "3"
    ) {
      console.log("1 Setting active key as 2 : Group members section");
      setActiveKey("2");
    } else if (
      groupInfo.status &&
      (groupInfo.status === "New" || groupInfo.status === "Draft") &&
      //groupInfo.amount > 0 &&
      !isAdmin &&
      groupInfo.registrationID &&
      String(groupInfo.registrationID).trim() !== ""
    ) {
      console.log("Setting active key as 3 : Payment section");
      setActiveKey("3");
    } else if (
      groupInfo.registrationID &&
      String(groupInfo.registrationID).trim() !== "" &&
      !isAdmin
    ) {
      console.log("2 Setting active key as 2 : Group members section");
      setActiveKey("2");
    } else {
      console.log("Setting active key as 1 : Main registration section");
      setActiveKey("1");
    }
  }, [
    isRegistrationAllowed,
    groupInfo.registrationID,
    groupInfo.status,
    //groupInfo.amount,
    isAdmin,
  ]);

  const saveDraft = async (groupInfo, isUnload = false) => {
    // console.log("Saving draft:", groupInfo);
    console.log("isUnload:", isUnload);
    console.log("userType:", userType);
    if ((userType === "G" && !otpVerified) || mode === "ADMIN") {
      //console.log("OTP not verified. Skipping save operation.");
      console.log("OTP not verified or ADMIN. Skipping save operation.", mode);
      return false;
    }
    if (!isRegistrationAllowed || newRegistrationStatus === "Blocked") {
      console.log("Registration is blocked. Skipping save operation.");
      return false;
    }

    if (
      !groupInfo.email ||
      groupInfo.email === "" ||
      !groupInfo.phone ||
      groupInfo.phone === "" ||
      ((!groupInfo.firstName || groupInfo.firstName === "") &&
        (!groupInfo.lastName || groupInfo.lastName === ""))
    ) {
      console.log("Key fields are missing. Skipping save operation.");
      return false;
    }
    //console.log("111 Saving draft to DB :", groupInfo);
    // Add a default member only if status is not "Cancelled" or "To Be Cancelled"
    if (
      (groupInfo.groupMembers.length === 0 ||
        groupInfo.groupMembers.every((member) => member.deleted)) &&
      groupInfo.status !== "To Be Cancelled" &&
      groupInfo.status !== "Cancelled"
    ) {
      console.log("Adding default member to groupMembers");
      setGroupInfo((prev) => ({
        ...prev,
        groupMembers: [
          {
            memberID: "",
            firstName: prev.firstName,
            lastName: prev.lastName,
            email: prev.email,
            age: "",
            gender: "",
            phone: prev.phone,
            relation: "Myself",
            comments: "",
            deleted: false,
            retreatID: id ? id : idMenuQuery,
          },
        ],
      }));
    }
    const nonDeletedCount = groupInfo.groupMembers.filter(
      (member) => !member.deleted
    ).length;
    setGroupInfo((prev) => ({
      ...prev,
      numberOfParticipants: nonDeletedCount,
    }));
    // Only save if there is a data change
    const changedFields = UtilMethods.deepDiff(groupInfo, originalGroupInfo);
    console.log("Changed fields:", changedFields);
    if (
      changedFields.length === 0 &&
      groupInfo.status !== "Draft" &&
      groupInfo.registrationID !== ""
    ) {
      console.log("111 No changes detected. Skipping save operation.");
      return false;
    }
    if (changedFields.length === 0 && groupInfo.registrationID !== "") {
      console.log("222 No changes detected. Skipping save operation.");
      return false;
    }
    try {
      //console.log("Saving group data as draft:", groupInfo);
      console.log("isUnload:", isUnload);
      console.log("saveMode:", isUnload ? "UNLOAD" : "SAVE");
      //const saveMode = isUnload ? "UNLOAD" : "SAVE";
      const response = await EventSupport.saveGroupData(groupInfo, "SAVE");
      console.log("Draft save response:", response);
      if (response && response.registrationID) {
        console.log(
          "Draft saved successfully with ID:",
          response.registrationID
        );
        return true;
      } else {
        console.error("Error saving draft:", response);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
    }
    return false;
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      console.log("Before unload event triggered");
      const changedFields = UtilMethods.deepDiff(groupInfo, originalGroupInfo);
      console.log("Changed fields:", changedFields);
      if (
        changedFields.length > 0 ||
        groupInfo.status === "Draft" ||
        groupInfo.registrationID === ""
      ) {
        console.log("Changes detected, saving draft before unload");
        // Use sendBeacon-safe mode

        saveDraft(groupInfo, true);
        // Trigger browser confirm dialog
        event.preventDefault();
        event.returnValue = "";
      }
      if (mode === "GUEST") {
        console.log("handleCancel Evt Reg Page, logout GUEST MODE");
        logout();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [
    groupInfo,
    originalGroupInfo,
    mode,
    logout,
    newRegistrationStatus,
    userType,
    otpVerified,
    isRegistrationAllowed,
  ]);

  const handleCloseModal = () => {
    setShowErrorModal(false);
    setError(null);
  };
  // Function to handle the link click and show the modal
  const handleShowConsentModal = () => {
    setModalType("retreat"); // Set the appropriate type
    setShowConsentModal(true); // Show the modal
  };

  const showAlert = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => {
      setAlertMessage("");
      setAlertType("");
    }, 5000);
  };
  // Function to close the modal
  const handleCloseConsentModal = (waiverSignedNew) => {
    console.log("handleCloseConsentModal Waiver signed:", waiverSignedNew);
    setGroupInfo((prev) => ({
      ...prev,
      waiverSigned: waiverSignedNew,
    }));
    setShowConsentModal(false); // Hide the modal
  };
  const handleCheckChange = (e) => {
    setGroupInfo((prev) => ({
      ...prev,
      waiverSigned: e.target.checked,
    }));
  };

  const handleCloseSuccessModal = () => {
    console.log("handleCloseSuccessModal called : saveMode:", saveMode);
    setShowSuccessModal(false);
    setSuccessMessage("");
    if (saveMode === "PAYMENT") {
      setActiveKey("3"); // Switch to Payment accordion
    } else {
      if (isAdmin) {
        console.log("Navigating back to Retreats page...");
        handleCancel();
      } else if (groupInfo && groupInfo.registrationID) {
        console.log("Proceeding to Waiver Form...");
        const baseUrl = process.env.REACT_APP_URL || window.location.origin;
        const linkW = `${baseUrl}/waiver-form?registrationID=${groupInfo.registrationID}&retreatID=${groupInfo.retreatID}`;
        console.log("Navigating to Waiver Form:", linkW);
        window.location.href = linkW;
      } else {
        console.log("Navigating back admin");
        handleCancel();
      }
    }
  };
  const isCancelDisabled = () =>
    !groupInfo?.registrationID ||
    (typeof groupInfo.registrationID === "string" &&
      groupInfo.registrationID.trim() === "") ||
    groupInfo.status === "Cancelled" ||
    groupInfo.status === "Draft";
  const isDisabled = () =>
    !groupInfo?.registrationID ||
    (typeof groupInfo.registrationID === "string" &&
      groupInfo.registrationID.trim() === "");
  /**
   * Handles the save confirmation process.
   * Closes the save confirmation modal, logs the group data, and attempts to save the group data.
   * If the registration is successful, displays a success message and modal.
   * If an error occurs during registration, displays an error message and modal.
   *
   * @async
   * @function handleSaveConfirm
   * @returns {Promise<void>}
   */
  const handleSaveConfirm = async () => {
    setShowSaveConfirmModal(false); // Close modal

    //console.log("Saving group data:", groupInfo);

    try {
      console.log("Group members being registered:", groupInfo.groupMembers);

      let paymentReference = [
        groupInfo.retreatID ? `ID:${groupInfo.retreatID}` : "",
        groupInfo.registrationID ? `-${groupInfo.registrationID},` : "",
        groupInfo.firstName && groupInfo.lastName
          ? `Name: ${groupInfo.firstName} ${groupInfo.lastName}`
          : "",
        groupInfo.phone ? `,Phone: ${groupInfo.phone}` : "",
      ]
        .filter(Boolean)
        .join("");

      const updatedGroupInfo = groupInfo
        ? {
          ...groupInfo,
          status: groupInfo.status === "Draft" ? "New" : groupInfo.status,
          paymentReference: paymentReference || "",
        }
        : groupInfo;

      setGroupInfo(updatedGroupInfo);
      setOriginalGroupInfo({ ...updatedGroupInfo });
      console.log("Final group status to be saved:", updatedGroupInfo?.status);
      const response = await EventSupport.saveGroupData(updatedGroupInfo); // Await the response from saveGroupData
      setGroupInfo(updatedGroupInfo);
      setOriginalGroupInfo({ ...updatedGroupInfo });
      //console.log("Registration response:", response);
      console.log("Registration response.error:", response.error);
      if (!response.error && response.registrationID) {
        console.log(
          "Registration successful! ",
          response.message + " With Id : " + response.registrationID
        );
        const emailData = {
          fullName:
            updatedGroupInfo.firstName + " " + updatedGroupInfo.lastName,
          email: updatedGroupInfo.email,
          phone: updatedGroupInfo.phone,
          amount:
            updatedGroupInfo.paymentMethod === "Zelle"
              ? updatedGroupInfo.amount
              : updatedGroupInfo.paymentMethod === "Stripe"
                ? EventSupport.calculateAndApplyStripeFees(
                  Number(updatedGroupInfo.amount),
                  eventData
                )
                : updatedGroupInfo.amount,
          paymentMethod: updatedGroupInfo.paymentMethod,
          status: updatedGroupInfo.status,
          isApproved:
            updatedGroupInfo.status === "Approved" ||
              updatedGroupInfo.status === "Confirmed"
              ? "Yes"
              : "No",
          groupMembers:
            UtilMethods.formatGroupMembers(updatedGroupInfo.groupMembers) + "",
          registrationID: response.registrationID,
          paymentOptions: eventData.paymentOptions,
          retreatEmail: eventData.retreatEmail,
          retreatName: eventData.name,
          whatsAppLink: eventData.whatsAppLink,
          paymentReference: updatedGroupInfo.paymentReference,
          paymentDetails:
            updatedGroupInfo.status === "Wait List"
              ? "Once seats are available we will notify you with payment details"
              : (updatedGroupInfo.paymentMethod === "Zelle"
                ? `Please send the pending amount of ${updatedGroupInfo.amount - updatedGroupInfo.paidAmount
                } via Zelle to: ${eventData.zelle_email || "info@graceanointing.org"
                }, give the above reference in the payment details/memo for easy identification. Zelle URL: ${eventData.zelle_URL
                } `
                : updatedGroupInfo.paymentMethod === "Stripe"
                  ? `Please pay the pending amount(including Transaction fees) of ${EventSupport.calculateAndApplyStripeFees(
                    Number(updatedGroupInfo.amount) -
                    Number(updatedGroupInfo.paidAmount),
                    eventData
                  )} using the Stripe link: ${eventData.stripe_URL}`
                  : ` Please complete the pending payment of $${updatedGroupInfo.amount - updatedGroupInfo.paidAmount
                  } using the selected payment method (${updatedGroupInfo.paymentMethod
                  })`) + " . Please ignore if already fully paid.",
        };
        const emailResponse = await sendEmail(
          emailData,
          "retreat",
          "service_retreat"
        );
        if (emailResponse.success) {
          setSuccessMessage(
            response.message +
            " with Registration ID : " +
            (response.registrationID || "N/A") +
            ", And Email confirmation sent to " +
            updatedGroupInfo.email +
            (updatedGroupInfo.amount - updatedGroupInfo.paidAmount > 0
              ? ` | Please pay the remaining payment of $${(
                updatedGroupInfo.amount - updatedGroupInfo.paidAmount
              ).toFixed(2)}.`
              : "") +
            " | Please Proceed to Submit the Consent/Waiver Form, for you and the group." +
            (newRegistrationStatus === "Wait List" &&
              updatedGroupInfo.registrationID === ""
              ? " | The status is WAIT LIST. Contact retreat@graceanointing.org for confirmation."
              : "")
          );
        } else {
          setSuccessMessage(
            response.message +
            " with Registration ID : " +
            response.registrationID +
            (updatedGroupInfo.amount - updatedGroupInfo.paidAmount > 0
              ? ` | Please pay the remaining payment of $${(
                updatedGroupInfo.amount - updatedGroupInfo.paidAmount
              ).toFixed(2)}.`
              : "") +
            " | Please Proceed to Submit the Consent/Waiver Form, for you and the group." +
            (newRegistrationStatus === "Wait List" &&
              updatedGroupInfo.registrationID === ""
              ? " | The status is WAIT LIST. Contact retreat@graceanointing.org for confirmation."
              : "")
          );
        }
        console.log("Email response:", emailResponse);
        //console.log("saveMode :", saveMode);
        if (saveMode === "PAYMENT") {
          console.log("handleSaveClick called from handleSaveConfirm");
          // Fetch group data
          const groupDataSaved = await EventSupport.fetchGroupData(
            updatedGroupInfo.retreatID,
            updatedGroupInfo.email,
            userData,
            mode
          );
          console.log("Group data saved:", groupDataSaved);
          if (groupDataSaved) {
            setGroupInfo(groupDataSaved);
            setOriginalGroupInfo({ ...groupDataSaved }); // Clone the group data for tracking changes
          }
        }
        setShowSuccessModal(true);

        //handleCancel();
      } else {
        console.error("Error during registration:", response);
        console.log("Error during registration:", response.error);
        console.log("Error message:", response.error.message);
        console.log("Error data:", response.error.data);
        console.log("Error message:", response.message);
        console.log("Error data:", response.data);
        setError(
          "Error during registration | " +
          String(response.error) +
          " | " +
          response.message +
          " | " +
          JSON.stringify(response.data)
        );
        setShowErrorModal(true); // Show error modal
      }
    } catch (error) {
      console.error("Final Error during registration:", error);
      setError(error.message);
      setShowErrorModal(true); // Show error modal
    }
  };

  const isValidData = () => {
    if (isAdmin) return true;
    return (
      validateMandatoryFields() &&
      validateMinimumAge() &&
      validateKidsCount() &&
      validateEmergencyContact()
    );
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    if (!groupInfo.registrationID || groupInfo.registrationID === "") {
      setError("Registrations are not save and hence cannot be cancelled.");
      setShowErrorModal(true); // Show error modal if registration is approved
      return;
    }
    setShowCancelConfirmModal(true); // Show confirmation modal on Cancel button click
  };

  /**
   * Handles the cancellation of a group registration.
   * Closes the cancellation confirmation modal, updates the group status to "Cancelled",
   * and displays appropriate success or error messages.
   *
   * @async
   * @function handleCancelRegistration
   * @returns {Promise<void>} - A promise that resolves when the cancellation process is complete.
   */
  const handleCancelRegistration = async () => {
    if (groupInfo.status === "To Be Cancelled") {
      if (groupInfo.paidAmount > 0) {
        setError(
          "Issue refund before confirming cancellation. Paid Amount : " +
          group.paidAmount +
          " | Refund Amount : " +
          group.refundAmount
        );
        setShowCancelConfirmModal(false); // Close modal
        setShowErrorModal(true);
        setActiveKey("3");
        return;
      }
    }
    setShowCancelConfirmModal(false); // Close modal
    //console.log("cancelRegistration group data:", groupInfo);
    try {
      const response = await EventSupport.updateGroupStatus(
        groupInfo,
        "Cancelled"
      );
      if (response) {
        if (response?.group?.status === "Cancelled") {
          const emailData = {
            fullName: groupInfo.firstName + " " + groupInfo.lastName,
            email: groupInfo.email,
            phone: groupInfo.phone,
            amount: groupInfo.amount,
            paymentMethod: groupInfo.paymentMethod,
            status: response?.group?.status || "Cancelled",
            groupMembers: UtilMethods.formatGroupMembers(
              groupInfo.groupMembers
            ),
            registrationID: response?.group?.registrationID || "NA",
            eventData: eventData,
            retreatName: eventData.name,
            whatsAppLink: eventData.whatsAppLink,
            paymentReference: groupInfo.paymentReference,
            paymentDetails: "Please contact the Church for refund details.",
            retreatEmail: eventData.retreatEmail,
          };
          const emailResponse = await sendEmail(
            emailData,
            "retreatCancel",
            "service_retreat"
          );
          if (emailResponse.success) {
            setSuccessMessage(
              response.message +
              " Registration ID : " +
              response.registrationID +
              ", And Email sent to " +
              groupInfo.email
            );
          }
        } else {
          setSuccessMessage(
            response.message + " Registration ID : " + response.registrationID
          );
        }
        setShowSuccessModal(true);
        handleCancel();
      } else {
        setError("Error during cancellation.");
        setShowErrorModal(true); // Show error modal
      }
    } catch (error) {
      console.error("Error during registration:", error);
      setError(
        "Error during registration: " +
        (error.response?.data?.message || error.message)
      );
      setShowErrorModal(true); // Show error modal
    }
  };
  const validateMandatoryFields = (mode) => {
    const {
      firstName,
      lastName,
      email,
      phone,
      emergencyContactNumber,
      emergencyContactPersonName,
      paymentMethod,
      groupMembers,
      waiverSigned,
      address,
      city,
      state,
      zip,
      country,
    } = groupInfo;

    const missingFields = [];
    const missingFieldsChild = [];
    let duplicateMembers = new Set();
    console.log("validateMandatoryFields mode:", mode);

    if (!firstName) missingFields.push("First Name");
    if (!lastName) missingFields.push("Last Name");
    if (!email) missingFields.push("Email");
    if (!phone) missingFields.push("Phone");
    if (mode === "PAYMENT" && !paymentMethod)
      missingFields.push("Payment Method");
    if (!emergencyContactNumber) missingFields.push("Emergency Contact Number");
    if (!emergencyContactPersonName)
      missingFields.push("Emergency Contact Person Name");
    if (!address) missingFields.push("Address");
    if (!city) missingFields.push("City");
    if (!state) missingFields.push("State");
    if (!zip) missingFields.push("Zip");
    if (!country) missingFields.push("Country");

    let nonDeletedMemberCount = 0;
    groupMembers.forEach((member, index) => {
      if (!member.deleted) {
        nonDeletedMemberCount++;
        // Ensure all fields are strings to avoid undefined issues
        const age = member.age ? String(member.age).trim() : "";
        const firstName = member.firstName
          ? String(member.firstName).trim()
          : "";
        const lastName = member.lastName ? String(member.lastName).trim() : "";
        const memberIdentifier =
          `${age}-${firstName}-${lastName}`.toUpperCase();
        if (
          duplicateMembers.has(memberIdentifier) &&
          age &&
          firstName &&
          lastName
        ) {
          missingFieldsChild.push(
            `Duplicate Member: ${firstName} ${lastName} with age ${age}`
          );
        } else {
          duplicateMembers.add(memberIdentifier);
        }
        if (!firstName || firstName.trim() === "")
          missingFieldsChild.push(`Member ${index + 1} - First Name`);
        if (!lastName || lastName.trim() === "")
          missingFieldsChild.push(`Member ${index + 1} - Last Name`);
        if (member.age > 16) {
          if (!member.email || member.email.trim() === "")
            missingFieldsChild.push(`Member ${index + 1} - Email`);
          if (!member.phone || member.phone.trim() === "")
            missingFieldsChild.push(`Member ${index + 1} - Phone`);
        }
        if (!member.age || isNaN(Number(member.age)) || member.age < 1) {
          missingFieldsChild.push(
            `Member ${index + 1} - Age must be greater than or equal to 1`
          );
        }

        if (!member.age || isNaN(Number(member.age)) || member.age > 100) {
          missingFieldsChild.push(
            `Member ${index + 1} - Age must be less than or equal to 100`
          );
        }
        if (!member.gender || member.gender.trim() === "") {
          missingFieldsChild.push(`Member ${index + 1} - Gender`);
        }
        if (!member.relation || member.relation.trim() === "") {
          missingFieldsChild.push(`Member ${index + 1} - Relation`);
        }
      }
    });

    if (nonDeletedMemberCount < 1) {
      missingFieldsChild.push("At least one member is required");
    }
    console.log("Missing Fields:", missingFields);

    if (missingFields.length > 0) {
      setError(
        `Please fill in the following mandatory fields: ${missingFields.join(
          ", "
        )}`
      );
      console.log(
        "Setting active key as 1 : Missing mandatory fields in main form"
      );
      setActiveKey("1");
      return false;
    }
    if (missingFieldsChild.length > 0) {
      setError(
        `Please fill in the following mandatory fields for group members: ${missingFieldsChild.join(
          ", "
        )}`
      );
      console.log(
        "Setting active key as 2 : Missing mandatory fields in group members"
      );
      setActiveKey("2");
      return false;
    }

    return true;
  };

  const validateMandatoryFieldsForApproval = () => {
    const { paymentMethod, paymentInfo, paidAmount } = groupInfo;

    const missingFields = [];

    // Check if any of the main group fields are missing
    if (!paymentInfo) missingFields.push("Payment Info");
    if (!paidAmount) missingFields.push("Paid Amount");
    if (!paymentMethod) missingFields.push("Payment Method");

    if (missingFields.length > 0) {
      setError(
        `Please fill in the following mandatory fields: ${missingFields.join(
          ", "
        )}`
      );
      console.log(
        "Setting active key as 3 : Missing mandatory fields in payment"
      );
      setActiveKey("3");
      return false;
    }

    return true;
  };

  /**
   * Handles the approval process for a group registration.
   *
   * This function performs the following steps:
   * 1. Validates mandatory fields and fields required for approval.
   * 2. Updates the group status to "Approved" using the EventSupport service.
   * 3. Sends a confirmation email to the group contact if the approval is successful.
   * 4. Displays success or error messages based on the outcome of the approval process.
   *
   * @async
   * @function handleApprove
   * @returns {Promise<void>}
   * @throws Will throw an error if the approval process fails.
   */
  const handleApprove = async () => {
    if (validateMandatoryFields() && validateMandatoryFieldsForApproval()) {
      try {
        //console.log("handleApprove :", groupInfo);

        const response = await EventSupport.updateGroupStatus(
          groupInfo,
          "Confirmed"
        ); // Await the response from update Grp
        console.log("Approval response:", response);
        if (response) {
          const emailData = {
            fullName: groupInfo.firstName + " " + groupInfo.lastName,
            email: groupInfo.email,
            phone: groupInfo.phone,
            amount:
              groupInfo.paymentMethod === "Zelle"
                ? groupInfo.amount
                : groupInfo.paymentMethod === "Stripe"
                  ? EventSupport.calculateAndApplyStripeFees(
                    Number(groupInfo.amount),
                    eventData
                  )
                  : groupInfo.amount,
            paymentMethod: groupInfo.paymentMethod,
            status: groupInfo.status,
            groupMembers:
              UtilMethods.formatGroupMembers(groupInfo.groupMembers) + "",
            registrationID: groupInfo.registrationID,
            retreatName: eventData.name,
            whatsAppLink: eventData.whatsAppLink,
            paymentReference: groupInfo.paymentReference,
            paymentDetails:
              groupInfo.status === "Wait List"
                ? "Once seats are available we will notify you with payment details"
                : (groupInfo.paymentMethod === "Zelle"
                  ? `Please send the pending amount of ${groupInfo.amount - groupInfo.paidAmount
                  } via Zelle to: ${eventData.zelle_email || "info@graceanointing.org"
                  }, give the above reference in the payment details/memo for easy identification. Zelle URL: ${eventData.zelle_URL
                  }`
                  : groupInfo.paymentMethod === "Stripe"
                    ? `Please pay the pending amount(including Transaction fees) of ${EventSupport.calculateAndApplyStripeFees(
                      Number(groupInfo.amount) - Number(groupInfo.paidAmount),
                      eventData
                    )} using the Stripe link: ${eventData.stripe_URL}`
                    : ` Please complete the pending payment of ${groupInfo.amount - groupInfo.paidAmount
                    } using the selected payment method (${groupInfo.paymentMethod
                    }).`) +
                "Please Make sure payment is completed within 2 weeks. Registration will be confirmed upon payment verification",

            paidAmount: groupInfo.paidAmount,
            whatsAppLink: eventData.whatsAppLink,
            retreatEmail: eventData.retreatEmail,
          };
          const emailResponse = await sendEmail(
            emailData,
            "retreatApproval",
            "service_retreat"
          );
          if (emailResponse.success) {
            setSuccessMessage(
              `${response.message} Registration ID : ${response?.group?.registrationID || "NA"
              }, And Email confirmation sent to ${groupInfo.email
              } | Continue to Submit the Waiver Form, for you and the group.`
            );
            if (groupInfo.amount > groupInfo.paidAmount) {
              setSuccessMessage(
                `${response.message} Registration ID : ${response?.group?.registrationID || "NA"
                }, And Email confirmation sent to ${groupInfo.email
                }. Please collect the remaining payment of $${(
                  groupInfo.amount - groupInfo.paidAmount
                ).toFixed(2)}.`
              );
            } else {
              setSuccessMessage(
                `${response.message} Registration ID : ${response?.group?.registrationID || "NA"
                }, And Email confirmation sent to ${groupInfo.email}`
              );
            }
            setSuccessMessage(
              `${response.message} Registration ID : ${response?.group?.registrationID || "NA"
              }, And Email confirmation sent to ${groupInfo.email}`
            );
          } else {
            setSuccessMessage(
              response.message +
              " Registration ID : " +
              response?.group?.registrationID || "NA"
            );
          }
          setShowSuccessModal(true);
          // handleCancel();
        } else {
          setError("Error during approval.");
          setShowErrorModal(true); // Display error modal if validation fails
        }
      } catch (error) {
        console.error("Error during registration:", error);
        setError(
          "Error during approval: " +
          (error.response?.data?.message || error.message)
        );
        setShowErrorModal(true);
      }
    } else {
      setShowErrorModal(true); // Display error modal if validation fails
    }
  };

  const handleAddMember = () => {
    // console.log("handleAddMember Evt Reg Page, ", groupInfo);

    if (validateMandatoryFields("ADD")) {
      setGroupInfo((prev) => ({
        ...prev,
        groupMembers: [
          ...prev.groupMembers,
          {
            memberID: "",
            firstName: "",
            lastName: "",
            email: "",
            age: "",
            gender: "",
            phone: "",
            relation: "",
            comments: "",
            deleted: false,
            retreatID: id ? id : idMenuQuery,
          },
        ],
        numberOfParticipants: prev.numberOfParticipants + 1, // Increment numberOfParticipants
      }));
      setEditingMemberIndex(groupInfo.groupMembers.length);
      console.log(
        "Setting active key as 2 : Group Members section after adding member."
      );
      setActiveKey("2"); // Open the "Group Members" accordion (index-based)
    } else {
      setShowErrorModal(true); // Display error modal if validation fails
    }
    //console.log("handleAddMember Evt Reg Page End, ", groupInfo);
  };

  const handleInputChange = (field, value) => {
    setGroupInfo((prev) => ({
      ...prev,
      [field]:
        field === "rooms" || field === "numberOfParticipants"
          ? Number(value)
          : value,
    }));
  };

  const handleDeleteMember = (index, isDeleted) => {
    console.log("handleDeleteMember Evt Reg Page isDeleted :", isDeleted);
    const updatedMembers = groupInfo.groupMembers
      .map((member, i) => {
        if (i === index) {
          // If the member has an ID, mark it as deleted
          if (member.memberID) {
            return { ...member, deleted: isDeleted };
          }
          // Otherwise, remove the member by filtering it out
          return null;
        }
        return member;
      })
      .filter(Boolean); // Remove null entries

    // Calculate the number of non-deleted members
    let nonDeletedMemberCount = updatedMembers.filter(
      (member) => !member.deleted
    ).length;
    if (nonDeletedMemberCount === 0) {
      updatedMembers.push({
        memberID: "",
        firstName: "",
        lastName: "",
        email: "",
        age: "",
        gender: "",
        phone: "",
        relation: "",
        comments: "",
        deleted: false,
        retreatID: id ? id : idMenuQuery,
      });
      nonDeletedMemberCount = 1;
    }

    // Update group members and number of participants
    setGroupInfo((prev) => ({
      ...prev,
      groupMembers: updatedMembers,
      numberOfParticipants: nonDeletedMemberCount,
    }));

    // Recalculate the amount separately
    const amount = EventSupport.calculateAmount({
      ...groupInfo,
      groupMembers: updatedMembers,
    });
    if (
      (groupInfo.status === "Approved" ||
        groupInfo.status === "Paid" ||
        groupInfo.status === "Confirmed") &&
      groupInfo.paidAmount >= amount
    ) {
      const refundAmount = Math.abs(amount - groupInfo.paidAmount);
      setGroupInfo((prev) => ({
        ...prev,
        refundAmount,
      }));
    }
    // Update the amount
    setGroupInfo((prev) => ({
      ...prev,
      amount,
    }));
  };

  const handleMemberChange = (index, field, value) => {
    if (!Array.isArray(groupInfo.groupMembers)) {
      setError("Group members data is missing or corrupt.");
      return;
    }
    const updatedMembers = [...groupInfo.groupMembers];
    updatedMembers[index][field] = field === "age" ? Number(value) : value;
    if (field === "age") {
      updatedMembers[index]["isEdited"] = true;

      let amount = EventSupport.calculateAmount(groupInfo);
      // Update the amount
      setGroupInfo((prev) => ({
        ...prev,
        amount,
      }));
      if (
        (groupInfo.status === "Approved" ||
          groupInfo.status === "Paid" ||
          groupInfo.status === "Confirmed") &&
        groupInfo.paidAmount >= amount
      ) {
        const refundAmount = Math.abs(amount - groupInfo.paidAmount);
        setGroupInfo((prev) => ({
          ...prev,
          refundAmount,
        }));
      }
    }
    setGroupInfo((prev) => ({
      ...prev,
      groupMembers: updatedMembers,
    }));
  };

  const validateMinimumAge = () => {
    const { groupMembers } = groupInfo;
    const hasAdult = groupMembers.some(
      (member) => !member.deleted && member.age >= 18
    );

    if (!hasAdult) {
      console.log(
        "validateMinimumAge Setting active key as 2 : At least one group member must be at least 18 years old."
      );
      setActiveKey("2");
      setError("At least one group member must be at least 18 years old.");
      return false;
    }

    return true;
  };

  const validateKidsCount = () => {
    const { groupMembers } = groupInfo;
    // Check for any new member (memberID !== '' and age < 19)
    const hasNewKid = groupMembers.some(
      (member) => !member.deleted && (member.memberID !== "" || (groupInfo.status === "Draft")) && (member.age>5 && member.age < 19)
    );

    if (hasNewKid) {
      console.log(
        "validateKidsCount Setting active key as 2 : New member under 19 detected. Children's registration is closed."
      );
      setActiveKey("2");
      setError("We have reached the maximum capacity for the children’s sessions. We regret that we are unable to accept further registrations for Children's sessions.");
      return false;
    }

    return true;
  };
  const validateEmergencyContact = () => {
    if (!isAdmin) {
      // to be removed later after retreat
      const { groupMembers } = groupInfo;
      if (groupInfo.phone === groupInfo.emergencyContactNumber) {
        setError(
          "Emergency contact number cannot be the same as the registration's phone number."
        );
        setActiveKey("1");
        return false;
      }
      for (let i = 0; i < groupMembers.length; i++) {
        if (groupMembers[i].deleted) continue;
        if (groupMembers[i].phone === groupInfo.emergencyContactNumber) {
          setError(
            `Emergency contact number cannot be the same as the member's phone number for member ${i + 1
            }.`
          );
          console.log(
            "Setting active key as 1 : Emergency contact number validation failed."
          );
          setActiveKey("1");
          return false;
        }
      }
    }
    return true;
  };
  /**
   * Handles the save button click event.
   *
   * This function performs the following actions:
   * 1. Logs the group data to the console.
   * 2. Validates that at least one group member is at least 18 years old.
   * 3. Validates mandatory fields.
   * 4. If validations pass, shows the save confirmation modal.
   * 5. If validations fail, shows the error modal.
   *
   * @async
   * @function handleSaveClick
   * @returns {void}
   */
  // mode: "PAYMENT" if called from PaymentAccordion, otherwise undefined
  const handleSaveClick = async (mode = "MAIN") => {
    // console.log("Save Button Click group data:", groupInfo);
    // Add API call for saving data
    console.log("Save Group button clicked mode:", mode);
    console.log("Save Group button clicked saveMode:", saveMode);
    setSaveMode(mode); // Set save mode based on the caller
    if (userType === "G") {
      if (!otpVerified) {
        setError(
          "Please verify OTP and provide the registration details before saving the registration."
        );
        setShowErrorModal(true);
        return;
      }
    }

    console.log("validateMandatoryFields mode:", mode);
    // Validate mandatory fields and minimum age
    if (
      groupInfo.status !== "Cancelled" &&
      groupInfo.status !== "To Be Cancelled"
    ) {
      if (
        validateMandatoryFields(mode) &&
        validateMinimumAge() &&
        validateKidsCount() &&
        validateEmergencyContact()
      ) {
        setShowSaveConfirmModal(true);
      } else {
        setShowErrorModal(true);
      }
    } else {
      if (isAdmin) {
        // Additional validation for admin users
        setShowSaveConfirmModal(true);
      } else {
        setError(
          "Cannot save registration as it is marked Cancelled or To Be Cancelled."
        );
        setShowErrorModal(true);
      }
    }
  };

  const handleCancel = () => {
    console.log("handleCancel Evt Reg Page, idMenuQuery", idMenuQuery);
    console.log("handleCancel Evt Reg Page, id", id);
    setSuccessMessage(null);
    setError(null);
    console.log("handleCancel Evt Reg Page, parentFilters", parentFilters);
    if (mode === "GUEST") {
      console.log("handleCancel Evt Reg Page, logout GUEST MODE");
      logout();
    }
    if (idMenuQuery) {
      navigate("/", { replace: true }); // Navigate to home if idMenuQuery is available
    } else {
      // Navigate back to the parent screen, preserving the filters
      if (parentFilters) {
        navigate("/admin_retreat-registration", {
          state: { filters: parentFilters },
        });
      } else {
        navigate(-1);
      }
    }
  };
  const isSaveDisabled = () => {
    // Admins can always save
    if (isAdmin) return false;

    // If groupInfo is not loaded treat as disabled to avoid runtime errors
    if (!groupInfo) return true;

    const status = groupInfo?.status || "";
    const retreatID = groupInfo?.retreatID ?? "";

    // Disable for cancelled states
    if (status === "Cancelled" || status === "To Be Cancelled") {
      if (
        newRegistrationStatus === "Blocked" ||
        newRegistrationStatus === "Wait List"
      ) {
        return true;
      } else {
        return false;
      }
    }

    // Disable when event registration is blocked
    if (newRegistrationStatus === "Blocked") {
      return true;
    }

    // If event is on Wait List, disable save for existing groups that are Paid/Confirmed/Approved
    if (
      newRegistrationStatus === "Wait List" &&
      ["Paid", "Confirmed", "Approved"].includes(status)
    ) {
      return true;
    } else if (
      newRegistrationStatus === "Wait List" &&
      status === "Wait List" &&
      retreatID !== "0" &&
      retreatID !== ""
    ) {
      return false;
    }

    // Disable if registration not allowed or invalid retreatID
    if (!isRegistrationAllowed) return true;
    if (retreatID === "0" || retreatID === "") return true;

    return false;
  };
  const handleAccordionToggle = (key) => {
    console.log("handleAccordionToggle", key);
    if (activeKey !== key) {
      console.log("handleAccordionToggle Setting active key to", key);
      setActiveKey(key);
      if (key === "2") {
        console.log("handleAccordionToggle Setting editing member index to 0");
        setEditingMemberIndex(0);
      }
      if (
        key &&
        accordionRef.current &&
        typeof accordionRef.current.scrollIntoView === "function"
      ) {
        accordionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };
  return (
    <div>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>
      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/KrupabhishekamBibleConvention.png)`,
          }}
        >
          {eventData ? (
            <>
              <div className="banner-heading text-center wow fadeInDown">
                <h1 className="text-white">Register for {eventData.name}</h1>
              </div>
            </>
          ) : (
            <div className="banner-heading text-center wow fadeInDown">
              <h1 className="text-white">Retreat Registration</h1>
            </div>
          )}
        </div>
      </div>
      <Container className="mt-4">
        {loading && (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && (
          <>
            <Accordion
              ref={accordionRef}
              activeKey={activeKey}
              onSelect={(key) => handleAccordionToggle(key)}
            >
              {/* Event Details */}
              {eventData ? (
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <h4>
                      <FontAwesomeIcon icon={faStar} className="me-2" />
                      ID: {eventData.id} {" | "} {eventData.name}
                      {" | "} {eventData.start_date}
                    </h4>
                    <span
                      style={{
                        color: orange[500],
                        animation: "blink 1s infinite",
                        textAlign: "center",
                        fontStyle: "italic",
                        textDecoration: "underline",
                      }}
                    >
                      {" "}
                      Click to view details {" > "}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        {" "}
                        <p style={{ fontSize: "1.2rem" }} className="mb-1">
                          <i className="fa-solid fa-calendar"></i>
                          <strong> Dates:</strong> {eventData.start_date} to{" "}
                          {eventData.end_date} {"  "}
                          <FontAwesomeIcon icon={faFileAlt} />
                          <a
                            href={`${process.env.PUBLIC_URL}/images/Retreat_${eventData.id}.jpg`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {" : "}
                            Flyer
                          </a>
                        </p>
                      </Col>
                      <Col md={6}>
                        {" "}
                        <p style={{ fontSize: "1.2rem" }} className="mb-1">
                          <i className="fa-solid fa-map"></i>{" "}
                          <strong> Location:</strong>{" "}
                          <a
                            href={`${eventData.map}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {eventData.location}
                          </a>
                        </p>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        {UtilMethods.formatTextWithLineBreaks(
                          eventData.description
                        )}{" "}
                      </Col>
                    </Row>
                    <Row>
                      {eventData.reg_type === "NO" ? (
                        <p style={{ color: "red" }}>
                          <strong>
                            Registration is{" "}
                            <span style={{ textDecoration: "underline" }}>
                              Not Required
                            </span>{" "}
                            for this event. You can directly come to the venue.
                          </strong>
                        </p>
                      ) : null}
                      <span style={{ color: "orange", fontWeight: "bold" }}>
                        {" We have reached the maximum capacity for the children’s sessions. We regret that we are unable to accept further registrations for Children's sessions."}
                      </span>
                      {newRegistrationStatus !== "Draft" && (
                        <p
                          style={{
                            color:
                              newRegistrationStatus === "Wait List"
                                ? "orange"
                                : "red",
                          }}
                        >
                          <strong>
                            {newRegistrationStatus === "Wait List" ? (
                              <>
                                Registration is currently on{" "}
                                <span style={{ textDecoration: "underline" }}>
                                  Wait List
                                </span>{" "}
                                status. Please email the details to
                                retreat@graceanointing.org or contact any of the
                                below contacts for more information.
                              </>
                            ) : (
                              <>
                                Registration is{" "}
                                <span>
                                  {eventData?.reg_date &&
                                    new Date() < new Date(eventData.reg_date) ? (
                                    <>
                                      <span
                                        style={{ textDecoration: "underline" }}
                                      >
                                        Not Open
                                      </span>
                                      . Registration will open on{" "}
                                      {eventData.reg_date}.
                                    </>
                                  ) : (
                                    <>
                                      <span
                                        style={{ textDecoration: "underline" }}
                                      >
                                        Closed
                                      </span>
                                      . WALK-INS will NOT BE PERMITTED{" "}
                                      {/*Please write to
                                      retreat@graceanointing.org or contact any
                                      of the below contacts for more
                                      information.*/}
                                    </>
                                  )}
                                </span>{" "}
                              </>
                            )}
                          </strong>
                        </p>

                      )}
                      <hr />
                    </Row>
                    <Row className="mt-1 mb-0">
                      <Col md={6}>
                        <>
                          <i className="fa-solid fa-clock"></i>{" "}
                          <strong> Time :</strong>{" "}
                          {UtilMethods.formatTextWithLineBreaks(
                            eventData.timings
                          )}
                        </>
                      </Col>
                      <Col md={6}>
                        <>
                          <i className="fa-solid fa-phone"></i>{" "}
                          <strong>Contact Details : </strong> <br />
                          {UtilMethods.formatTextWithLineBreaks(
                            eventData.contact
                          )}{" "}
                          Email:{" "}
                          <a href={`mailto:${eventData.retreatEmail}`}>
                            {eventData.retreatEmail}
                          </a>
                        </>
                      </Col>
                      <hr />
                    </Row>

                    {eventData?.type === "Residential" ? (
                      // Residential-specific content
                      <Row className="mt-1 mb-1">
                        <Col md={6}>
                          <strong>Fee Details</strong>
                          <p>
                            <strong>Single Person (No Sharing):</strong>
                          </p>
                          <ul>
                            <li>
                              <p>
                                Total Fee: ${" "}
                                {(
                                  (eventData.roomBasePrice || 0) +
                                  (eventData.adultRetreatRate || 0)
                                ).toFixed(2)}{" "}
                                (includes ${eventData.roomBasePrice || 0} base
                                fee + ${eventData.adultRetreatRate || 0}{" "}
                                additional)
                              </p>
                            </li>
                          </ul>
                        </Col>
                        <Col md={6}>
                          <p>
                            <strong>Shared Accommodation:</strong>
                          </p>
                          <ul>
                            <li>
                              First 2 Participants (Adult/Kid): $
                              {eventData.roomBasePrice || 0} each
                            </li>
                            <li>
                              Additional Adults: $
                              {eventData.additionalAdultRate || 0} each
                            </li>
                            <li>
                              Additional Kids (Ages 5-18): $
                              {eventData.childRate || 0} each
                            </li>
                            <li>
                              Kids Under 5:{" "}
                              {eventData.toddlerRate > 0
                                ? `$${eventData.toddlerRate}`
                                : "Free"}
                            </li>
                          </ul>
                        </Col>
                        <Col>
                          <p>
                            <strong>Payment Options:</strong>
                          </p>

                          {eventData.paymentOptions ? (
                            <p>{eventData.paymentOptions}</p>
                          ) : (
                            <p>No payment options available.</p>
                          )}
                        </Col>
                      </Row>
                    ) : (
                      // Convention-specific content
                      <>
                        <Row className="mt-0 mb-1">
                          <Col md={6}>
                            <FontAwesomeIcon icon={faDollar} />
                            <strong> Fee Details :</strong>
                            {/* Grouped Fee Details by Amount */}
                            {eventData && (
                              <ul className="mt-1 mb-1">
                                {(() => {
                                  // Prepare an array of fee categories and their amounts
                                  const feeCategories = [
                                    {
                                      label: "Adult (Ages 19+)",
                                      amount: eventData.adultRetreatRate || 0,
                                    },
                                    {
                                      label: "Youth (Ages 16-18)",
                                      amount: eventData.youngAdultRate || 0,
                                    },
                                    {
                                      label: "Teen (Ages 13-15)",
                                      amount: eventData.teenRate || 0,
                                    },
                                    {
                                      label: "Kids (Ages 5-12)",
                                      amount: eventData.childRate || 0,
                                    },
                                    {
                                      label: "Kids Under 5",
                                      amount: eventData.toddlerRate || 0,
                                    },
                                  ];

                                  // Group by amount
                                  const grouped = {};
                                  feeCategories.forEach(({ label, amount }) => {
                                    const key =
                                      amount > 0 ? `$${amount}` : "Free";
                                    if (!grouped[key]) grouped[key] = [];
                                    grouped[key].push(label);
                                  });

                                  // Render grouped items
                                  return Object.entries(grouped).map(
                                    ([amount, labels]) => (
                                      <li key={amount}>
                                        <strong>{amount}:</strong>{" "}
                                        {labels.join(", ")}
                                      </li>
                                    )
                                  );
                                })()}
                              </ul>
                            )}
                          </Col>
                          <Col>
                            <FontAwesomeIcon icon={faMoneyBill} />
                            <strong> Payment Options : </strong>
                            <br />
                            {eventData.paymentOptions ? (
                              <>{eventData.paymentOptions}</>
                            ) : (
                              <>No payment options available.</>
                            )}
                          </Col>
                        </Row>
                        <Row>
                          <Col md={12}>
                            <strong>Notes : </strong>
                            {eventData.notes}
                          </Col>
                        </Row>
                        {isRegistrationAllowed && !userData && (
                          <Row className="mt-1">
                            <Col
                              xs={12}
                              className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-2"
                            >
                              <p
                                style={{
                                  fontWeight: "bold",
                                  marginBottom: "0",
                                }}
                              >
                                Click here to register : 👉
                              </p>
                              <Button
                                variant="primary"
                                onClick={() => {
                                  if (isRegistrationAllowed && !userData) {
                                    localStorage.setItem(
                                      "previousPath",
                                      location.pathname
                                    );
                                    navigate(
                                      `/login?${id ? `id=${id}` : `id=${idMenuQuery}`
                                      }`
                                    );
                                  } else {
                                    setActiveKey("1");
                                  }
                                }}
                              >
                                Register
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <h4>Event Details</h4>
                  </Accordion.Header>
                  <Accordion.Body>
                    <p>No event details available.</p>
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* Group Data */}
              {(isRegistrationAllowed || isAdmin) && userData && (
                <>
                  <GroupRegistrationAccordion
                    groupInfo={groupInfo}
                    editingMemberIndex={editingMemberIndex}
                    otpVerified={otpVerified}
                    userType={userType}
                    handleInputChange={handleInputChange}
                    handleAddMember={handleAddMember}
                    eventData={eventData}
                    handleApprove={handleApprove}
                    setGroupInfo={setGroupInfo}
                    setActiveKey={setActiveKey}
                    setEditingMemberIndex={setEditingMemberIndex}
                    setOTPVerified={setOTPVerified}
                    setOriginalGroupInfo={setOriginalGroupInfo}
                  />

                  {/* Group Members Section */}
                  <GroupMembersAccordion
                    groupInfo={groupInfo}
                    isEditEnabled={
                      (userType !== "G" ||
                        (userType === "G" && otpVerified) ||
                        isAdmin) &&
                      isSaveDisabled() === false
                    }
                    handleMemberChange={handleMemberChange}
                    handleDeleteMember={handleDeleteMember}
                    handleAddMember={handleAddMember}
                    editingMemberIndex={editingMemberIndex}
                    setEditingMemberIndex={setEditingMemberIndex}
                    setGroupInfo={setGroupInfo}
                  />
                  <PaymentAccordion
                    groupInfo={groupInfo}
                    eventData={eventData}
                    setGroupInfo={setGroupInfo}
                    setOriginalGroupInfo={setOriginalGroupInfo}
                    onAccordionHeaderClick={async () => {
                      console.log("Payment Accordion Header Clicked");
                      if (!isValidData()) {
                        setShowErrorModal(true);
                        return false;
                      } else {
                        if (
                          newRegistrationStatus === "Wait List" &&
                          groupInfo.status === "Draft" &&
                          groupInfo.registrationID &&
                          String(groupInfo.registrationID).trim() !== ""
                        ) {
                          // Move draft to Wait List when event is waitlisted and registration exists
                          groupInfo.status = "Wait List"; // ensure saveDraft sees the updated status
                          setGroupInfo((prev) => ({
                            ...prev,
                            status: "Wait List",
                          }));
                        }
                        const isSaved = await saveDraft(groupInfo);
                        console.log(
                          "handleSaveClick called from handleSaveConfirm"
                        );
                        // Fetch group data
                        if (isSaved) {
                          const groupDataSaved =
                            await EventSupport.fetchGroupData(
                              groupInfo.retreatID,
                              groupInfo.email,
                              userData,
                              mode
                            );
                          console.log("Group data saved:", groupDataSaved);
                          if (groupDataSaved) {
                            setGroupInfo((prev) => ({
                              ...prev,
                              registrationID: groupDataSaved.registrationID,
                            }));
                            setGroupInfo(groupDataSaved);

                            setOriginalGroupInfo(
                              structuredClone(groupDataSaved)
                            ); // Deep clone the group data for tracking changes
                            console.log(
                              "Setting active key as 3 : Group data after saving and fetching:",
                              groupInfo.registrationID
                            );
                            console.log(
                              "groupDataSaved.registrationID : ",
                              groupDataSaved.registrationID
                            );
                            console.log(
                              "groupInfo.registrationID : ",
                              groupInfo.registrationID
                            );
                            console.log(
                              "originalGroupInfo.registrationID : ",
                              originalGroupInfo.registrationID
                            );
                            setActiveKey("3"); // Open the Payment accordion
                          }
                        }
                        return true;
                      }
                    }}
                    handleInputChange={handleInputChange}
                    handleSaveClick={handleSaveClick}
                    isSaveDisabled={isSaveDisabled}
                    isAdmin={isAdmin}
                    isValidData={isValidData}
                  />
                </>
              )}
            </Accordion>
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 mt-4 mb-3">
              {/* Waiver Form Section */}
              <div className="d-flex align-items-center gap-2">
                {/*}
                <a
                  href={""}
                  onClick={(e) => {
                    e.preventDefault();
                    handleShowConsentModal();
                  }}
                  className="text-decoration-none"
                >
                  Consent to Use Data
                </a>

                <input
                  type="checkbox"
                  className="form-check-input"
                  name="waiverSigned"
                  value={groupInfo?.waiverSigned}
                  checked={groupInfo?.waiverSigned}
                  onChange={handleCheckChange}
                  required
                />*/}
                {/*}
                <a
                  href={waiverForm}
                  download="Krupabhishekam_Retreat_2025_Waiver.pdf"
                  className="download-link ms-3"
                  aria-label="Download Waiver Form"
                >
                  <button
                    className="btn btn-info"
                    title="Download Waiver Form and fill it and bring it to the retreat"
                    aria-label="Download Waiver Form"
                  >
                    <FontAwesomeIcon icon={faFileAlt} /> Download Waiver Form
                  </button>
                </a>*/}
                {groupInfo.status === "Paid" &&
                  Number(groupInfo.amount) <= Number(groupInfo.paidAmount) && (
                    <a
                      href={
                        groupInfo.registrationID
                          ? `${process.env.REACT_APP_URL}/waiver-form?registrationID=${groupInfo.registrationID}&retreatID=${groupInfo.retreatID}`
                          : "#"
                      }
                      className={`btn btn-info download-link ms-3${!groupInfo?.registrationID ||
                        groupInfo?.registrationID === "" ||
                        groupInfo?.status !== "Paid"
                        ? ""
                        : " disabled"
                        }`}
                      aria-label="Submit Waiver Form"
                      tabIndex={groupInfo.registrationID ? 0 : -1}
                      style={{
                        pointerEvents: groupInfo.registrationID
                          ? "auto"
                          : "none",
                        opacity: groupInfo.registrationID ? 1 : 0.5,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      title="Click and sign the Consent Form online, for you and the group."
                      role="button"
                      rel="noopener noreferrer"
                      target="_blank"
                      disabled={
                        !groupInfo?.registrationID ||
                          groupInfo?.registrationID === "" ||
                          groupInfo?.status !== "Paid"
                          ? true
                          : false
                      }
                      aria-disabled={
                        !groupInfo?.registrationID ||
                          groupInfo?.registrationID === "" ||
                          groupInfo?.status !== "Paid"
                          ? true
                          : false
                      }
                      onClick={
                        !groupInfo.registrationID
                          ? (e) => e.preventDefault()
                          : undefined
                      }
                    >
                      <FontAwesomeIcon icon={faFileAlt} /> Submit Consent Form
                    </a>
                  )}
              </div>
              {/* Cancel Button */}
              {groupInfo.registrationID && groupInfo.registrationID !== "" && (
                <Button
                  variant="btn btn-primary"
                  onClick={handleCancelConfirm}
                  disabled={isCancelDisabled()}
                  aria-disabled={isCancelDisabled()}
                  title={
                    isCancelDisabled()
                      ? "No registration to cancel"
                      : "Click to cancel registration"
                  }
                  aria-label={
                    isCancelDisabled()
                      ? "No registration to cancel"
                      : "Cancel registration"
                  }
                >
                  <FontAwesomeIcon icon={faBan} />{" "}
                  {groupInfo.status === "To Be Cancelled"
                    ? "Confirm Cancel"
                    : "Cancel Reg"}
                </Button>
              )}

              {/* Approve Button (Admin only) */}
              {isAdmin && (
                <Button
                  variant="btn btn-success"
                  onClick={handleApprove}
                  disabled={!isAdmin || isDisabled()}
                  aria-disabled={!isAdmin || isDisabled()}
                  title={
                    isDisabled()
                      ? "Registration not ready for Confirmation"
                      : "Click to Confirm registration"
                  }
                  aria-label={
                    isDisabled()
                      ? "Registration not ready for Confirmation"
                      : "Confirm registration"
                  }
                >
                  <FontAwesomeIcon icon={faCheck} /> Confirm
                </Button>
              )}

              {/* Save Button */}
              {(["Paid", "Approved", "Confirmed", "Wait List"].includes(
                groupInfo.status
              ) ||
                isAdmin) && (
                  <Button
                    variant="btn btn-primary"
                    onClick={handleSaveClick}
                    disabled={isSaveDisabled()}
                    aria-disabled={isSaveDisabled()}
                    title={
                      isSaveDisabled()
                        ? "Cannot save because the registration is " +
                        groupInfo.status
                        : "Click to save registration"
                    }
                    aria-label={
                      isSaveDisabled()
                        ? "Cannot save because the registration is " +
                        groupInfo.status
                        : "Save registration"
                    }
                  >
                    <FontAwesomeIcon icon={faSave} /> Submit Registration
                  </Button>
                )}

              {/* Back Button */}
              <Button variant="btn btn-secondary" onClick={handleCancel}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </Button>
            </div>
          </>
        )}
      </Container>
      <FooterPanel />

      {/* Success Modal */}
      <AlertModal
        show={showSuccessModal}
        title="Registration Successful"
        message={successMessage}
        type="success"
        details={{}} // or pass additional details if necessary
        onCancel={handleCloseSuccessModal}
      />
      {/* Error Modal */}

      <AlertModal
        show={showErrorModal}
        title="Error in Retreat Registration"
        type="error"
        message={error}
        details={{}}
        onCancel={handleCloseModal}
      />

      {/* Render the ConsentModal */}
      <ConsentModal
        show={showConsentModal}
        type={modalType}
        consentData={{
          title: eventData?.short_description || "",
          name: groupInfo.firstName + " " + groupInfo.lastName,
          waiverSigned: groupInfo.waiverSigned,
          date: groupInfo.createdAt,
        }}
        onCancel={(waiverSigned) => handleCloseConsentModal(waiverSigned)}
      />
      {/* Save Confirmation Modal */}

      <AlertModal
        show={showSaveConfirmModal}
        title="Confirm Retreat Registration"
        type="confirmation"
        message={
          newRegistrationStatus === "Wait List" && !groupInfo.registrationID
            ? "You will be in WAIT LIST status. Are you sure you want to submit the below details for Retreat ? On Confirmation, you will receive an email with the details."
            : "Are you sure you want to submit the below details for Retreat?"
        }
        details={{
          Email: groupInfo.email,
          Phone: groupInfo.phone,
          Count: groupInfo.numberOfParticipants,
          Members: UtilMethods.formatGroupMembers(groupInfo.groupMembers),
        }}
        onCancel={() => setShowSaveConfirmModal(false)}
        onConfirm={handleSaveConfirm}
      />
      {/* Cancel Confirmation Modal */}
      <AlertModal
        show={showCancelConfirmModal}
        title="Cancel Retreat Registration"
        type="confirmation"
        message="Are you sure you want to cancel the Retreat Registration?"
        details={{
          Email: groupInfo.email,
          Phone: groupInfo.phone,
          Count: groupInfo.numberOfParticipants,
          Members: UtilMethods.formatGroupMembers(groupInfo.groupMembers),
        }}
        onCancel={() => setShowCancelConfirmModal(false)}
        onConfirm={handleCancelRegistration}
      />
    </div>
  );
};

export default EventRegistrationPage;
